import { useContainerWidth } from "./useContainerWidth";

interface LineChartProps {
  /** Series points in chronological order. */
  data: { d: string; v: number }[];
  /** Render height in px (width fills the parent via ResizeObserver). */
  height?: number;
  /** Stroke + fill color for the line. */
  color?: string;
  /** Optional label below the chart. */
  caption?: string;
  /** When true, also draws a soft area fill under the line. */
  filled?: boolean;
}

/**
 * Line chart for admin list pages. Larger than `Sparkline` — has axis ticks
 * on Y (max + 0) and date labels on X (first + last + a couple in between).
 * Hand-rolled SVG, but the viewBox tracks the real container width so text
 * isn't squashed by `preserveAspectRatio="none"`.
 */
export function LineChart({
  data,
  height = 220,
  color = "var(--color-accent)",
  caption,
  filled = true,
}: LineChartProps) {
  const [hostRef, hostWidth] = useContainerWidth();

  if (data.length === 0) {
    return (
      <div
        className="grid place-items-center rounded-2xl border border-border bg-surface text-sm text-text-tertiary"
        style={{ height }}
      >
        Sem dados no período.
      </div>
    );
  }

  const width = hostWidth > 0 ? hostWidth : 720;
  const padX = 40;
  const padTop = 16;
  const padBottom = 32;

  const values = data.map((p) => p.v);
  const max = Math.max(...values, 1);
  const chartW = width - padX * 2;
  const chartH = height - padTop - padBottom;
  const stepX = data.length > 1 ? chartW / (data.length - 1) : chartW;

  const points = data.map((p, i) => {
    const x = padX + i * stepX;
    const y = padTop + (1 - p.v / max) * chartH;
    return { x, y, p };
  });

  const linePath = points
    .map((pt, i) => `${i === 0 ? "M" : "L"}${pt.x.toFixed(2)},${pt.y.toFixed(2)}`)
    .join(" ");
  const areaPath = filled
    ? `${linePath} L${(padX + chartW).toFixed(2)},${padTop + chartH} L${padX},${padTop + chartH} Z`
    : null;

  // Pick ~5 X-axis ticks evenly spaced (always include first and last).
  const tickIndices: number[] = [];
  const tickCount = Math.min(5, data.length);
  for (let i = 0; i < tickCount; i++) {
    tickIndices.push(
      Math.round(((data.length - 1) * i) / Math.max(tickCount - 1, 1))
    );
  }

  function shortDate(iso: string): string {
    const dt = new Date(iso + "T00:00:00Z");
    return dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  }

  return (
    <div ref={hostRef} className="rounded-2xl border border-border bg-surface p-5">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        className="block max-w-full"
        role="img"
      >
        {/* Y baseline */}
        <line
          x1={padX}
          y1={padTop + chartH}
          x2={padX + chartW}
          y2={padTop + chartH}
          stroke="var(--color-border)"
          strokeWidth={1}
        />
        {/* Y max guide */}
        <line
          x1={padX}
          y1={padTop}
          x2={padX + chartW}
          y2={padTop}
          stroke="var(--color-border)"
          strokeDasharray="3 3"
          strokeWidth={1}
        />
        {areaPath && (
          <path d={areaPath} fill={color} fillOpacity={0.12} stroke="none" />
        )}
        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={1.75}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {/* Y labels */}
        <text
          x={padX - 8}
          y={padTop + 4}
          textAnchor="end"
          fontSize={11}
          fill="var(--color-text-tertiary)"
        >
          {max}
        </text>
        <text
          x={padX - 8}
          y={padTop + chartH + 4}
          textAnchor="end"
          fontSize={11}
          fill="var(--color-text-tertiary)"
        >
          0
        </text>
        {/* X labels */}
        {tickIndices.map((i, n) => {
          const pt = points[i];
          if (!pt) return null;
          return (
            <text
              key={n}
              x={pt.x}
              y={height - 10}
              textAnchor={
                i === 0 ? "start" : i === data.length - 1 ? "end" : "middle"
              }
              fontSize={11}
              fill="var(--color-text-tertiary)"
            >
              {shortDate(pt.p.d)}
            </text>
          );
        })}
      </svg>
      {caption && <p className="mt-2 text-xs text-text-tertiary">{caption}</p>}
    </div>
  );
}
